import React, { useState, useEffect } from 'react';
import { 
  Calendar, Users, Package, DollarSign, BarChart3, FileText, 
  CheckCircle, ArrowRight, Menu, X, MessageCircle, Phone,
  ChevronDown, Star, Shield, Zap, Clock, HeadphonesIcon,
  TrendingUp, PieChart, Receipt, Moon, Smartphone, Target,
  Award, Sparkles, Heart, BadgeCheck, Gift
} from 'lucide-react';
import { SoluClinicEvents } from './utils/facebookPixel';

// WhatsApp URLs
const WHATSAPP_NUMBER = '5571992248597';
const WHATSAPP_DEMO = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, gostaria de agendar uma demonstração do sistema SoluClinic.')}`;
const WHATSAPP_ENTERPRISE = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Tenho interesse no plano Enterprise do SoluClinic. Poderia me enviar mais detalhes e me ajudar a agendar uma demonstração?')}`;
const WHATSAPP_ENTERPRISE_MAX = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Tenho interesse no plano Enterprise Max do SoluClinic. Poderia me enviar mais detalhes e me ajudar a agendar uma demonstração?')}`;

const SECTION_FLOW = [
  'inicio',
  'dores',
  'beneficios',
  'funcionalidades',
  'mobile',
  'depoimentos',
  'diferenciais',
  'precos',
  'faq',
  'cta-final'
];

const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

const scrollToPrecos = (e) => {
  if (e?.preventDefault) e.preventDefault();
  scrollToSection('precos');
};

const getNextSectionId = () => {
  const currentY = window.scrollY + 140;
  const positions = SECTION_FLOW.map((id) => {
    const el = document.getElementById(id);
    if (!el) return { id, top: Number.POSITIVE_INFINITY };
    const rect = el.getBoundingClientRect();
    return { id, top: rect.top + window.scrollY };
  });

  let currentIndex = 0;
  for (let i = 0; i < positions.length; i++) {
    if (positions[i].top <= currentY) currentIndex = i;
  }

  const nextIndex = Math.min(currentIndex + 1, SECTION_FLOW.length - 1);
  return SECTION_FLOW[nextIndex];
};

const scrollToNextSection = (e) => {
  if (e?.preventDefault) e.preventDefault();
  scrollToSection(getNextSectionId());
};

// Scroll to top function
const scrollToTop = (e) => {
  e.preventDefault();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Desktop Mockup Component - MacBook Pro Style
const DesktopMockup = ({ src, alt, className = '' }) => (
  <div className={`relative ${className}`}>
    <div className="relative">
      {/* Screen frame - thin bezels */}
      <div className="bg-[#1d1d1f] rounded-lg p-1 shadow-2xl">
        {/* Notch */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-4 bg-[#1d1d1f] rounded-b-xl z-10 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-[#3d3d3f]"></div>
        </div>
        {/* Screen */}
        <div className="bg-[#1d1d1f] rounded-md overflow-hidden">
          <img src={src} alt={alt} className="w-full h-auto rounded-sm" />
        </div>
      </div>
      {/* Base */}
      <div className="bg-gradient-to-b from-[#3d3d3f] to-[#2d2d2f] h-3 w-[105%] -ml-[2.5%] rounded-b-xl relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/4 h-1 bg-[#4d4d4f] rounded-b"></div>
      </div>
    </div>
  </div>
);

// iPhone 15 Pro Max Mockup - Realistic proportions
const IPhoneMockup = ({ src, alt, className = '' }) => (
  <div className={`relative ${className}`}>
    {/* iPhone frame - Titanium Natural */}
    <div 
      className="relative bg-[#1d1d1f] p-[2px] shadow-2xl"
      style={{ borderRadius: '12%/6%' }}
    >
      {/* Inner frame */}
      <div 
        className="relative bg-black overflow-hidden"
        style={{ borderRadius: '11%/5.5%' }}
      >
        {/* Dynamic Island */}
        <div className="absolute top-[2%] left-1/2 -translate-x-1/2 w-[28%] h-[3.5%] bg-black rounded-full z-20"></div>
        {/* Screen */}
        <img src={src} alt={alt} className="w-full h-auto" />
        {/* Home indicator */}
        <div className="absolute bottom-[1%] left-1/2 -translate-x-1/2 w-[30%] h-[0.5%] bg-white/40 rounded-full z-20"></div>
      </div>
      {/* Side buttons */}
      <div className="absolute left-[-1px] top-[18%] w-[2px] h-[5%] bg-[#3d3d3f] rounded-l-sm"></div>
      <div className="absolute left-[-1px] top-[26%] w-[2px] h-[8%] bg-[#3d3d3f] rounded-l-sm"></div>
      <div className="absolute left-[-1px] top-[36%] w-[2px] h-[8%] bg-[#3d3d3f] rounded-l-sm"></div>
      <div className="absolute right-[-1px] top-[28%] w-[2px] h-[12%] bg-[#3d3d3f] rounded-r-sm"></div>
    </div>
  </div>
);

// Urgency Bar Component
const UrgencyBar = () => (
  <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary-600 via-primary-500 to-pink-500 text-white py-1.5 sm:py-2 text-center">
    <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center justify-center gap-2 sm:gap-3">
      <Gift size={14} className="flex-shrink-0 animate-pulse" />
      <p className="text-[11px] sm:text-xs font-semibold">
        <span className="hidden sm:inline">Oferta especial: </span>
        <strong>7 dias gratis</strong> + acesso total a todos os modulos
        <span className="hidden sm:inline"> — sem cartao de credito</span>
      </p>
      <button 
        onClick={scrollToPrecos}
        className="flex-shrink-0 bg-white text-primary-600 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold hover:bg-primary-50 transition-all"
      >
        Testar agora
      </button>
    </div>
  </div>
);

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-[28px] sm:top-[32px] left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg py-2' : 'bg-transparent py-3'
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Mobile: Menu button on left */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Logo - centered on mobile, left on desktop */}
          <button
            type="button"
            onClick={scrollToTop}
            className="absolute left-1/2 -translate-x-1/2 lg:relative lg:left-0 lg:translate-x-0"
          >
            <img src="/logo-dark.png" alt="SoluClinic" className="h-7 sm:h-9 md:h-10" />
          </button>
          
          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            <button 
              onClick={scrollToPrecos}
              className="bg-gradient-to-r from-primary-400 to-primary-600 text-white px-5 py-2 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all text-sm"
            >
              Testar 7 Dias Gratis
            </button>
          </nav>

          {/* Mobile: CTA button on right */}
          <button 
            onClick={scrollToPrecos}
            className="lg:hidden bg-gradient-to-r from-primary-400 to-primary-600 text-white px-3 py-1.5 rounded-full font-semibold text-xs"
          >
            Testar Gratis
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-3 pb-3 flex flex-col gap-2 bg-white rounded-xl p-3 shadow-lg">
            <button 
              onClick={(e) => { setIsMobileMenuOpen(false); scrollToPrecos(e); }}
              className="bg-gradient-to-r from-primary-400 to-primary-600 text-white px-5 py-2.5 rounded-full font-semibold text-center text-sm mt-2"
            >
              Testar 7 Dias Gratis
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
  <section id="inicio" className="relative min-h-screen flex items-center bg-gradient-to-br from-primary-50 via-white to-primary-100 pt-20 sm:pt-24">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-48 sm:w-80 h-48 sm:h-80 bg-primary-200 rounded-full opacity-30 blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-48 sm:w-80 h-48 sm:h-80 bg-primary-300 rounded-full opacity-20 blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-16 relative z-10">
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
        <div className="animate-fade-in text-center lg:text-left">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-3 sm:mb-5">
            Você no Piloto da Sua{' '}
            <span className="text-gradient">Clínica de Estética</span>
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed max-w-2xl mx-auto lg:mx-0">
            Gerencie pacientes, agenda, estoque, financeiro e emita notas fiscais em um único lugar. 
            Aumente seu faturamento com decisões baseadas em dados.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6 justify-center lg:justify-start">
            <button 
              onClick={scrollToPrecos}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold text-sm sm:text-base hover:shadow-xl hover:scale-105 transition-all"
            >
              <Zap size={18} />
              Testar 7 Dias Gratis
            </button>
            <button
              type="button"
              onClick={scrollToNextSection}
              className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:border-primary-400 hover:text-primary-500 transition-all"
            >
              Ver como funciona
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Social Proof */}
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-5 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="text-green-500" size={16} />
              <span><strong>+139</strong> clinicas</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Star className="text-yellow-500 fill-yellow-500" size={16} />
              <span><strong>4.9/5</strong> avaliacao</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="text-primary-500" size={16} />
              <span><strong>Sem</strong> cartao</span>
            </div>
          </div>
        </div>

        {/* Hero Image - Desktop + Mobile mockup */}
        <div className="relative animate-slide-up mt-4 lg:mt-0">
          {/* Desktop view: side by side */}
          <div className="hidden sm:flex items-center gap-6 lg:gap-10 justify-center">
            {/* Desktop mockup */}
            <div className="transform hover:scale-[1.02] transition-transform duration-500 flex-1 max-w-[68%]">
              <DesktopMockup src="/dashboard.jpg" alt="Dashboard SoluClinic" />
            </div>
            {/* Mobile mockup */}
            <div className="flex-shrink-0 z-10 transform hover:scale-[1.02] transition-transform duration-500 w-32 md:w-40 lg:w-48">
              <IPhoneMockup src="/mobile-1.jpg" alt="SoluClinic Mobile" />
            </div>
          </div>
          
          {/* Mobile view: stacked vertically for better visibility */}
          <div className="sm:hidden flex flex-col items-center gap-6">
            {/* Desktop mockup - larger */}
            <div className="w-full">
              <DesktopMockup src="/dashboard.jpg" alt="Dashboard SoluClinic" />
            </div>
            {/* Mobile mockup - larger */}
            <div className="w-36">
              <IPhoneMockup src="/mobile-1.jpg" alt="SoluClinic Mobile" />
            </div>
          </div>
          
          {/* Floating elements - desktop only */}
          <div className="hidden sm:block absolute top-[5%] left-[35%] bg-green-500 text-white px-3 md:px-4 py-1.5 rounded-full text-xs font-bold shadow-lg animate-float">
            +40% Faturamento
          </div>
          <div className="hidden sm:flex absolute bottom-[15%] left-[8%] bg-white px-3 md:px-4 py-1.5 rounded-full text-xs font-bold shadow-lg items-center gap-2">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
            Sistema Online
          </div>
          <div className="hidden sm:flex absolute bottom-[20%] right-[2%] bg-white px-3 md:px-4 py-1.5 rounded-full text-xs font-bold shadow-lg items-center gap-2 z-20">
            <div className="w-2.5 h-2.5 bg-primary-500 rounded-full animate-pulse"></div>
            App Mobile
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

// Problems Section
const Problems = () => {
  const problems = [
    { icon: FileText, text: 'Planilhas desatualizadas' },
    { icon: Calendar, text: 'Agenda desorganizada' },
    { icon: DollarSign, text: 'Sem controle financeiro' },
    { icon: Package, text: 'Produtos vencendo' },
    { icon: Users, text: 'Pacientes não retornam' },
    { icon: Receipt, text: 'Dificuldade com NF' },
  ];

  return (
    <section id="dores" className="py-8 sm:py-12 lg:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Você ainda gerencia sua clínica assim?
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Se identificou com algum desses problemas?
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
          {problems.map((problem, index) => (
            <div 
              key={index}
              onClick={scrollToNextSection}
              className="flex flex-col items-center gap-2 p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl hover:shadow-md transition-shadow text-center cursor-pointer"
            >
              <div className="p-2 bg-red-100 rounded-full">
                <problem.icon className="text-red-500" size={18} />
              </div>
              <p className="text-gray-700 font-medium text-xs sm:text-sm">{problem.text}</p>
            </div>
          ))}
        </div>

        <p className="text-center mt-6 sm:mt-8 text-xs sm:text-sm text-gray-600 italic px-2">
          "Se você se identificou com pelo menos 2 desses problemas, o{' '}
          <span className="text-primary-500 font-semibold">SoluClinic</span> foi feito para você."
        </p>
      </div>
    </section>
  );
};

// Benefits Section
const Benefits = () => {
  const benefits = [
    { icon: BarChart3, title: 'Dashboard', description: 'Faturamento e vendas em tempo real', color: 'primary' },
    { icon: Target, title: 'CRM', description: 'Pipeline Kanban e funis de vendas', color: 'pink' },
    { icon: Calendar, title: 'Agenda', description: 'Visual intuitiva e fácil de usar', color: 'blue' },
    { icon: Users, title: 'Prontuário', description: 'Histórico completo com fotos', color: 'green' },
    { icon: DollarSign, title: 'Financeiro', description: 'Receitas, despesas e metas', color: 'yellow' },
    { icon: Package, title: 'Estoque', description: 'Alertas de validade e mínimo', color: 'purple' },
    { icon: Receipt, title: 'Nota Fiscal', description: 'NF-e e NFS-e integradas', color: 'indigo' },
    { icon: TrendingUp, title: 'Analytics', description: 'LTV e previsão de demanda', color: 'cyan' },
  ];

  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    cyan: 'bg-cyan-100 text-cyan-600',
  };

  return (
    <section id="beneficios" className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-gray-50 to-primary-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Tudo em um só sistema
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            Módulos completos para você focar no que importa: seus pacientes
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2 sm:gap-4">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              onClick={scrollToNextSection}
              className={`relative bg-white p-3 sm:p-4 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border ${benefit.isNew ? 'border-pink-300 ring-2 ring-pink-200' : 'border-gray-100'} cursor-pointer`}
            >
              {benefit.isNew && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                  NOVO
                </span>
              )}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${colorClasses[benefit.color]}`}>
                <benefit.icon size={20} />
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">{benefit.title}</h3>
              <p className="text-gray-600 text-xs leading-tight">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Stats Section - Social Proof with Numbers
const Stats = () => {
  const stats = [
    { number: '+139', label: 'Clinicas Ativas', sublabel: 'em todo o Brasil' },
    { number: '+50.000', label: 'Pacientes Gerenciados', sublabel: 'na plataforma' },
    { number: '99.9%', label: 'Uptime Garantido', sublabel: 'disponibilidade' },
    { number: '4.9/5', label: 'Avaliacao dos Clientes', sublabel: 'satisfacao' },
  ];

  return (
    <section className="py-10 sm:py-14 lg:py-16 bg-gradient-to-r from-primary-500 to-primary-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-1">{stat.number}</div>
              <div className="text-sm sm:text-base font-semibold text-white/90">{stat.label}</div>
              <div className="text-xs text-white/70">{stat.sublabel}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// How It Works Section - 3 Simple Steps
const HowItWorks = () => {
  const steps = [
    { number: '1', title: 'Cadastre-se em 2 minutos', desc: 'Crie sua conta gratuitamente, sem cartao de credito. Comece a testar imediatamente.' },
    { number: '2', title: 'Configure sua clinica', desc: 'Adicione seus procedimentos, profissionais e configure sua agenda. Nosso suporte ajuda voce.' },
    { number: '3', title: 'Gerencie tudo em um so lugar', desc: 'Pacientes, agenda, financeiro, estoque e notas fiscais. Tudo integrado e automatizado.' },
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Comece em 3 passos simples
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Sem complicacao, sem burocracia
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 sm:gap-10">
          {steps.map((step, index) => (
            <div key={index} className="relative text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <span className="text-2xl sm:text-3xl font-extrabold text-white">{step.number}</span>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary-300 to-transparent"></div>
              )}
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-sm sm:text-base text-gray-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section with Tabs and Autoplay
const Features = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  const features = [
    { id: 'dashboard', title: 'Dashboard', subtitle: 'Sua clínica em um olhar', icon: BarChart3, image: '/dashboard.jpg', bullets: ['Faturamento e vendas em tempo real', 'Top procedimentos mais lucrativos', 'Alertas de estoque e vencimentos', 'Agenda do dia com confirmações'] },
    { id: 'crm', title: 'CRM', subtitle: 'Gestão de Oportunidades e Vendas', icon: Target, image: '/crm-desktop-1.png', mobileImage: '/crm-mobile.jpg', bullets: ['Pipeline Kanban com arrastar e soltar', 'Funis e etapas personalizáveis', 'Acompanhamento de conversão e previsão de receita', 'Tarefas e atividades por oportunidade'] },
    { id: 'pacientes', title: 'Pacientes', subtitle: 'Prontuário digital completo', icon: Users, image: '/pacientes.jpg', bullets: ['Histórico completo de atendimentos', 'Galeria de fotos antes/depois', 'Termos de consentimento digitais', 'Orçamentos e pagamentos'] },
    { id: 'agenda', title: 'Agenda', subtitle: 'Nunca mais perca um agendamento', icon: Calendar, image: '/agenda.jpg', bullets: ['Visualização por dia, semana ou mês', 'Confirmação manual ou via WhatsApp', 'Bloqueio de horários', 'Lembretes para pacientes'] },
    { id: 'transcricao', title: 'Transcrição IA', subtitle: 'Grave consultas e gere resumos automáticos', icon: Zap, image: '/transcricao-ia.jpg', bullets: ['Grave a consulta com um clique', 'Transcrição automática por IA', 'Resumo estruturado da anamnese', 'Histórico completo por paciente'] },
    { id: 'teleconsulta', title: 'Teleconsulta', subtitle: 'Atenda pacientes de qualquer lugar', icon: Phone, image: '/teleconsulta.jpg', bullets: ['Videochamada integrada ao sistema', 'Link exclusivo para cada paciente', 'Chat em tempo real', 'Gravação opcional da consulta'] },
    { id: 'financeiro', title: 'Financeiro', subtitle: 'Controle total do seu dinheiro', icon: DollarSign, image: '/financeiro.jpg', bullets: ['Receitas e despesas categorizadas', 'Contas a pagar e receber', 'Metas financeiras com acompanhamento', 'Relatórios de lucratividade'] },
    { id: 'fiscal', title: 'Nota Fiscal', subtitle: 'Emissão integrada de NF-e e NFS-e', icon: Receipt, image: '/fiscal.jpg', bullets: ['Emissão com poucos cliques', 'Integrado ao módulo financeiro', 'Histórico de notas emitidas', 'Conformidade fiscal garantida'] },
    { id: 'estoque', title: 'Estoque', subtitle: 'Produtos sempre em dia', icon: Package, image: '/estoque.jpg', bullets: ['Controle de lotes e validade', 'Alertas de estoque mínimo', 'Histórico de movimentações', 'Relatório de consumo'] },
    { id: 'analytics', title: 'Analytics', subtitle: 'Decisões baseadas em dados', icon: PieChart, image: '/analytics.jpg', bullets: ['LTV (Lifetime Value) dos pacientes', 'Taxa de conversão de orçamentos', 'Previsão de demanda', 'Identificação de pacientes inativos'] },
  ];

  // Autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <section id="funcionalidades" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Conheça cada módulo do SoluClinic
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600">
            Clique para explorar as funcionalidades
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12">
          {features.map((feature, index) => (
            <button
              key={feature.id}
              onClick={() => setActiveTab(index)}
              className={`relative flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full font-medium transition-all text-xs sm:text-sm ${
                activeTab === index
                  ? 'bg-gradient-to-r from-primary-400 to-primary-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${feature.isNew ? 'ring-2 ring-primary-400 ring-offset-1' : ''}`}
            >
              {feature.isNew && (
                <span className="absolute -top-2 -right-1 bg-gradient-to-r from-primary-500 to-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  NOVO
                </span>
              )}
              <feature.icon size={16} />
              <span className="hidden sm:inline">{feature.title}</span>
            </button>
          ))}
        </div>

        {/* Progress bar */}
        <div className="flex justify-center gap-1 mb-8">
          {features.map((_, index) => (
            <div 
              key={index}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === activeTab ? 'w-8 bg-primary-500' : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
          <div className="order-2 lg:order-1">
            {features[activeTab].mobileImage ? (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 lg:gap-6">
                <div className="flex-1 w-full sm:max-w-[76%]">
                  <DesktopMockup 
                    src={features[activeTab].image} 
                    alt={features[activeTab].title}
                    className="shadow-xl"
                  />
                </div>
                <div className="flex-shrink-0 w-28 sm:w-32 md:w-40 lg:w-44">
                  <IPhoneMockup src={features[activeTab].mobileImage} alt={`${features[activeTab].title} Mobile`} />
                </div>
              </div>
            ) : (
              <DesktopMockup 
                src={features[activeTab].image} 
                alt={features[activeTab].title}
                className="shadow-xl"
              />
            )}
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-600 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              {React.createElement(features[activeTab].icon, { size: 18 })}
              {features[activeTab].title}
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              {features[activeTab].subtitle}
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {features[activeTab].bullets.map((bullet, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-gray-600 text-sm sm:text-base lg:text-lg">{bullet}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={scrollToPrecos}
              className="inline-flex items-center gap-2 mt-6 sm:mt-8 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:shadow-lg transition-all text-sm sm:text-base"
            >
              Testar 7 Dias Gratis
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mobile Section
const MobileSection = () => {
  const mobileImages = [
    '/mobile-1.jpg',
    '/mobile-2.jpg',
    '/mobile-3.jpg',
    '/mobile-4.jpg',
    '/mobile-5.jpg',
  ];

  return (
    <section id="mobile" className="py-10 sm:py-14 lg:py-16 bg-gradient-to-br from-primary-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-600 px-3 py-1.5 rounded-full text-xs font-semibold mb-3">
            <Smartphone size={14} />
            100% Responsivo
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
            Acesse de qualquer lugar
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            O SoluClinic funciona perfeitamente no seu celular. Gerencie sua clínica de onde estiver.
          </p>
        </div>

        {/* Mobile mockups carousel */}
        <div className="relative">
          {/* Desktop: show all 5 */}
          <div className="hidden md:flex gap-4 lg:gap-6 justify-center items-end">
            {mobileImages.map((img, index) => (
              <div 
                key={index}
                className={`flex-shrink-0 transition-all duration-500 ${
                  index === 2 ? 'scale-110 z-10' : 'scale-90 opacity-70'
                }`}
              >
                <div className="w-36 lg:w-44 xl:w-48">
                  <IPhoneMockup src={img} alt={`SoluClinic Mobile ${index + 1}`} />
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile: show 3 */}
          <div className="md:hidden flex gap-3 justify-center items-end">
            {mobileImages.slice(1, 4).map((img, index) => (
              <div 
                key={index}
                className={`flex-shrink-0 transition-all duration-500 ${
                  index === 1 ? 'scale-110 z-10' : 'scale-90 opacity-70'
                }`}
              >
                <div className="w-28 sm:w-32">
                  <IPhoneMockup src={img} alt={`SoluClinic Mobile ${index + 1}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <button 
            onClick={scrollToPrecos}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold text-sm sm:text-base hover:shadow-xl hover:scale-105 transition-all"
          >
            <Zap size={18} />
            Testar 7 Dias Gratis
          </button>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const testimonials = [
    { quote: 'Antes eu perdia 3 horas por semana com planilhas. Agora tenho tudo na palma da mao.', author: 'Dra. Marina Costa', role: 'Dermatologista, SP', rating: 5, initials: 'MC', color: 'from-pink-400 to-rose-500' },
    { quote: 'Aumentei meu faturamento em 35% so identificando os procedimentos mais lucrativos.', author: 'Clinica Harmonia', role: 'Estetica Avancada, RJ', rating: 5, initials: 'CH', color: 'from-purple-400 to-indigo-500' },
    { quote: 'A emissao de nota fiscal integrada foi um divisor de aguas. Economizo horas todo mes.', author: 'Dra. Juliana Mendes', role: 'Biomedica, MG', rating: 5, initials: 'JM', color: 'from-blue-400 to-cyan-500' },
    { quote: 'O controle de estoque me salvou de perder produtos vencidos. Ja recuperei o investimento no primeiro mes.', author: 'Clinica Bella Vita', role: 'Estetica Facial, BA', rating: 5, initials: 'BV', color: 'from-emerald-400 to-teal-500' },
    { quote: 'A teleconsulta integrada foi perfeita para atender pacientes de outras cidades. Minha agenda dobrou.', author: 'Dr. Ricardo Alves', role: 'Dermatologista, PR', rating: 5, initials: 'RA', color: 'from-amber-400 to-orange-500' },
    { quote: 'Finalmente consigo ver quanto realmente lucro em cada procedimento. Mudou minha forma de precificar.', author: 'Dra. Fernanda Lima', role: 'Esteticista, SC', rating: 5, initials: 'FL', color: 'from-primary-400 to-pink-500' },
  ];

  return (
    <section id="depoimentos" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-dark-900 to-dark-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            O que dizem nossos clientes
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300">
            Historias reais de clinicas que transformaram sua gestao
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/10 backdrop-blur-sm p-6 sm:p-8 rounded-xl sm:rounded-2xl border border-white/20 hover:bg-white/15 transition-all"
            >
              <div className="flex gap-1 mb-3 sm:mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                ))}
              </div>
              <p className="text-base sm:text-lg mb-4 sm:mb-6 text-gray-200 italic">"{testimonial.quote}"</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${testimonial.color} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white font-bold text-sm">{testimonial.initials}</span>
                </div>
                <div>
                  <p className="font-bold text-white text-sm sm:text-base">{testimonial.author}</p>
                  <p className="text-gray-400 text-xs sm:text-sm">{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Differentials Section
const Differentials = () => {
  const items = [
    { icon: Receipt, title: 'Nota Fiscal Integrada', desc: 'Emita NF-e e NFS-e sem sair do sistema' },
    { icon: Zap, title: 'Rápido e Leve', desc: 'Sistema otimizado, sem travamentos' },
    { icon: Moon, title: 'Modo Escuro', desc: 'Interface moderna que cuida dos seus olhos' },
    { icon: Clock, title: 'Atualizações Constantes', desc: 'Novas funcionalidades toda semana' },
    { icon: Shield, title: 'Dados Seguros', desc: 'Criptografia e backups diários' },
    { icon: HeadphonesIcon, title: 'Suporte Humanizado', desc: 'Atendimento via WhatsApp com especialistas' },
  ];

  return (
    <section id="diferenciais" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Por que escolher o SoluClinic?
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((item, index) => (
            <div
              key={index}
              onClick={scrollToNextSection}
              className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="p-2 sm:p-3 bg-primary-100 rounded-xl flex-shrink-0">
                <item.icon className="text-primary-600" size={22} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">{item.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Target Audience Section
const TargetAudience = () => {
  const audiences = [
    { icon: Sparkles, title: 'Esteticistas', desc: 'Profissionais autonomos que precisam organizar agenda, pacientes e financeiro em um so lugar.' },
    { icon: Heart, title: 'Clinicas de Estetica', desc: 'Clinicas de estetica facial e corporal que querem crescer com controle e profissionalismo.' },
    { icon: Award, title: 'Dermatologistas', desc: 'Medicos dermatologistas que realizam procedimentos esteticos e precisam de prontuario completo.' },
    { icon: BadgeCheck, title: 'Biomedicos Esteticistas', desc: 'Profissionais de bioestetica que precisam de termos de consentimento e registro de procedimentos.' },
    { icon: Users, title: 'Redes e Franquias', desc: 'Redes de clinicas que precisam de gestao multi-unidade com controle centralizado.' },
    { icon: TrendingUp, title: 'Clinicas em Crescimento', desc: 'Clinicas que estao saindo do papel e planilha e querem profissionalizar a gestao.' },
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-primary-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Para quem e o SoluClinic?
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Feito sob medida para profissionais da estetica
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {audiences.map((item, index) => (
            <div
              key={index}
              className="bg-white p-5 sm:p-6 rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100"
            >
              <div className="flex items-start gap-4">
                <div className="p-2.5 bg-primary-100 rounded-xl flex-shrink-0">
                  <item.icon className="text-primary-600" size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1.5 text-base">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <button 
            onClick={scrollToPrecos}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-full font-bold text-sm sm:text-base hover:shadow-xl hover:scale-105 transition-all"
          >
            <Zap size={18} />
            Testar 7 Dias Gratis
          </button>
        </div>
      </div>
    </section>
  );
};

// Guarantee Section
const Guarantee = () => (
  <section className="py-12 sm:py-16 bg-gradient-to-br from-green-50 to-emerald-50">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-xl border border-green-100">
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          <div className="text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Zap className="text-green-600" size={28} />
            </div>
            <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">7 Dias Gratis</h3>
            <p className="text-gray-600 text-sm">Teste todas as funcionalidades sem compromisso. Sem cartao de credito.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Shield className="text-green-600" size={28} />
            </div>
            <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Cancele Quando Quiser</h3>
            <p className="text-gray-600 text-sm">Sem fidelidade, sem multas. Voce tem total liberdade para cancelar a qualquer momento.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <HeadphonesIcon className="text-green-600" size={28} />
            </div>
            <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Suporte Humanizado</h3>
            <p className="text-gray-600 text-sm">Atendimento real via WhatsApp com especialistas em clinicas de estetica.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Pricing Section
const Pricing = () => {
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company_name: '', cpf_cnpj: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const plans = [
    {
      code: 'starter',
      name: 'Starter',
      price: '97',
      priceYearly: '970',
      description: 'Ideal para profissionais autônomos',
      features: ['1 usuário', 'Até 100 pacientes', 'Agenda de consultas', 'Cadastro de pacientes', 'Procedimentos básicos', 'Suporte por email'],
      popular: false
    },
    {
      code: 'professional',
      name: 'Profissional',
      price: '197',
      priceYearly: '1970',
      description: 'Para clínicas em crescimento',
      features: ['Até 3 usuários', 'Até 500 pacientes', 'Tudo do Starter +', 'Gestão financeira', 'Controle de estoque', 'Termos de consentimento', 'Tarefas e lembretes', 'Áudio nos agendamentos', 'Suporte prioritário'],
      popular: true
    },
    {
      code: 'business',
      name: 'Business',
      price: '397',
      priceYearly: '3970',
      description: 'Para clínicas estabelecidas',
      features: ['Até 10 usuários', 'Até 2.000 pacientes', 'Tudo do Profissional +', 'Transcrição com IA', 'Teleconsulta por vídeo', 'Emissão de NFS-e', 'Relatórios avançados', 'Analytics e métricas', 'Suporte dedicado'],
      popular: false
    },
    {
      code: 'enterprise',
      name: 'Enterprise',
      price: '797',
      priceYearly: '7970',
      description: 'Para redes de clínicas',
      features: ['Usuarios ilimitados', 'Pacientes ilimitados', 'Tudo do Business +', 'Recupere pacientes inativos automaticamente', 'Calculadora de Lucratividade por Procedimento', 'Gestao de Custos Fixos e Variaveis', 'Controle de Insumos e Materiais', 'CRM completo com Lead Scoring', 'Multi-unidades', 'Onboarding dedicado', 'Suporte prioritario', 'SLA garantido'],
      popular: false
    },
  ];

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowCheckoutModal(true);
    SoluClinicEvents.SelectPlan(plan.name, parseFloat(plan.price));
    
    // Disparar evento de conversão do Google Ads ao clicar em "Começar Grátis"
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-610832993/EsTwCKmT_fQbEOGkoqMC',
        'value': parseFloat(plan.price),
        'currency': 'BRL'
      });
    }
  };

  const handleSubmitCheckout = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Disparar evento de conversão do Google Ads no clique do botão
    if (typeof window.gtag_report_conversion === 'function') {
      window.gtag_report_conversion();
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-610832993/EsTwCKmT_fQbEOGkoqMC',
        'value': parseFloat(selectedPlan?.price || 1),
        'currency': 'BRL'
      });
    }

    try {
      // Primeiro criar o lead para tracking
      const leadResponse = await fetch('https://api.soluclinic.com.br/api/public/checkout/create-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          plan_code: selectedPlan.code,
          billing_cycle: billingCycle
        })
      });

      const leadData = await leadResponse.json();
      const leadId = leadData.success ? leadData.lead_id : null;

      // NOVA ESTRATÉGIA: Criar conta com trial GRATUITO de 7 dias SEM cartão
      const trialResponse = await fetch('https://api.soluclinic.com.br/api/public/checkout/create-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: leadId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          plan_code: selectedPlan.code,
          password: formData.password
        })
      });

      const trialData = await trialResponse.json();

      if (trialData.success && trialData.credentials) {
        // Conta criada com sucesso - 7 dias de trial gratuito
        SoluClinicEvents.SelectPlan(selectedPlan.name, parseFloat(selectedPlan.price));
        
        // Fechar modal de checkout e mostrar modal de sucesso
        setShowCheckoutModal(false);
        setSuccessData({
          ...trialData,
          plan: selectedPlan
        });
        setShowSuccessModal(true);
      } else {
        alert(trialData.error || 'Erro ao criar conta. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro no checkout:', error);
      alert('Erro ao processar. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccessSystem = () => {
    if (successData?.credentials?.login_url) {
      window.open(successData.credentials.login_url, '_blank');
    }
  };

  const getPrice = (plan) => billingCycle === 'yearly' ? Math.round(parseInt(plan.priceYearly) / 12) : parseInt(plan.price);

  return (
    <section id="precos" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-primary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Invista menos do que um procedimento por mês
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600">
            Escolha o plano ideal para sua clínica
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-white rounded-xl p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-all text-sm ${
                billingCycle === 'monthly'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm ${
                billingCycle === 'yearly'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>Anual</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                billingCycle === 'yearly' ? 'bg-white/20' : 'bg-green-100 text-green-700'
              }`}>
                2 meses grátis
              </span>
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative bg-white rounded-2xl p-5 sm:p-6 shadow-xl transition-all hover:shadow-2xl ${
                plan.popular ? 'ring-2 ring-primary-400 lg:scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                  MAIS POPULAR
                </div>
              )}
              
              <div className="text-center mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{plan.description}</p>
              </div>

              <div className="text-center mb-4">
                <span className="text-xs text-gray-500">R$</span>
                <span className="text-3xl sm:text-4xl font-extrabold text-gray-900">{getPrice(plan)}</span>
                <span className="text-gray-500 text-xs">/mes</span>
                <p className="text-xs text-gray-400 mt-1">
                  Apenas R$ {(getPrice(plan) / 30).toFixed(2).replace('.', ',')}/dia
                </p>
                {billingCycle === 'yearly' && (
                  <p className="text-xs text-green-600 mt-1">
                    R$ {plan.priceYearly}/ano (2 meses gratis)
                  </p>
                )}
              </div>

              <ul className="space-y-2 mb-5">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                    <span className="text-gray-600 text-xs">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleSelectPlan(plan)}
                className={`block w-full text-center py-2.5 sm:py-3 rounded-full font-bold text-sm transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-primary-400 to-primary-600 text-white hover:shadow-xl hover:scale-105'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Testar 7 Dias Gratis
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mt-6 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <Shield size={14} className="text-green-500" />
            <span>Sem cartao de credito</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap size={14} className="text-green-500" />
            <span>Ativacao em 2 minutos</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle size={14} className="text-green-500" />
            <span>Cancele quando quiser</span>
          </div>
        </div>
      </div>

      {/* Checkout Modal - Responsivo */}
      {showCheckoutModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Criar sua conta</h3>
              <button 
                onClick={() => setShowCheckoutModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="bg-primary-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-sm text-primary-700">
                <strong>Plano {selectedPlan.name}</strong>
                {' '}- R$ {getPrice(selectedPlan)}/mês
              </p>
              <p className="text-xs text-primary-600 mt-1">
                7 dias grátis para testar. Cancele quando quiser.
              </p>
            </div>

            <form onSubmit={handleSubmitCheckout} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seu nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  placeholder="Maria Silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  placeholder="maria@clinica.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Clínica</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  placeholder="Clínica Beleza & Saúde"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Crie sua senha *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-500 hover:to-primary-700 disabled:opacity-50 text-white py-3 sm:py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 text-base"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <span>Iniciar Teste Grátis</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Ao continuar, você concorda com nossos Termos de Uso e Política de Privacidade.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Sucesso Customizado - Responsivo */}
      {showSuccessModal && successData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full shadow-2xl transform animate-pulse-once max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-t-xl sm:rounded-t-2xl p-4 sm:p-6 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                <CheckCircle className="w-7 h-7 sm:w-10 sm:h-10 text-green-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">Conta Criada com Sucesso!</h3>
            </div>
            
            {/* Conteúdo */}
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Info do Trial */}
              <div className="bg-gradient-to-r from-primary-50 to-pink-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-primary-100">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Plano {successData.plan?.name}</p>
                    <p className="text-sm text-gray-600">{successData.trial_days || 7} dias grátis para testar</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Acesso gratuito até: <strong>{successData.trial_ends_at ? new Date(successData.trial_ends_at).toLocaleDateString('pt-BR') : 'em 7 dias'}</strong>
                </p>
                <p className="text-xs text-primary-600 mt-1">
                  Sem cartão de crédito necessário!
                </p>
              </div>

              {/* Credenciais */}
              <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2 sm:mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  Suas credenciais de acesso:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">{successData.credentials?.email}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border">
                    <span className="text-sm text-gray-600">Senha:</span>
                    <span className="font-medium text-gray-900">A que você definiu</span>
                  </div>
                </div>
              </div>

              {/* Aviso de email importante */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 text-base sm:text-lg">📧</span>
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Email Importante Enviado!</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Enviamos um email com o <strong>Guia de Primeiros Passos</strong> em anexo para ajudar você a começar.
                    </p>
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>Não encontrou? <strong>Verifique sua pasta de Spam</strong></span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="p-4 sm:p-6 pt-0 space-y-2 sm:space-y-3">
              <button
                onClick={handleAccessSystem}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white py-3 sm:py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base"
              >
                <span>Acessar o Sistema Agora</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm font-medium transition-colors"
              >
                Fechar e acessar depois
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// FAQ Section
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    { question: 'Preciso instalar algo?', answer: 'Não, o SoluClinic é 100% na nuvem. Acesse de qualquer navegador, em qualquer dispositivo, sem instalação.' },
    { question: 'Meus dados estão seguros?', answer: 'Sim! Utilizamos criptografia de ponta a ponta e realizamos backups diários. Seus dados estão protegidos com os mais altos padrões de segurança.' },
    { question: 'Posso migrar meus dados de outro sistema?', answer: 'Sim, nossa equipe oferece suporte completo para migração de dados. Ajudamos você a transferir todas as informações de forma segura.' },
    { question: 'Funciona no celular?', answer: 'Sim! O sistema é totalmente responsivo. Acesse do computador, tablet ou celular com a mesma experiência.' },
    { question: 'Como funciona a emissão de nota fiscal?', answer: 'A emissão é integrada ao módulo financeiro. Com poucos cliques você emite NF-e e NFS-e, mantendo tudo organizado e em conformidade fiscal.' },
    { question: 'Como funciona o suporte?', answer: 'Oferecemos suporte via WhatsApp com consultores especializados em clínicas de estética. Estamos sempre prontos para ajudar!' },
    { question: 'Quanto tempo leva para configurar?', answer: 'Em menos de 10 minutos voce ja pode comecar a usar. O sistema e intuitivo e nosso suporte ajuda na configuracao inicial.' },
    { question: 'Posso cancelar a qualquer momento?', answer: 'Sim! Nao ha fidelidade nem multas. Voce pode cancelar quando quiser, sem burocracia.' },
    { question: 'Voces oferecem treinamento?', answer: 'Sim! Alem do suporte, enviamos um guia completo de primeiros passos e oferecemos treinamento personalizado para planos Business e Enterprise.' },
    { question: 'O que acontece apos o periodo de teste?', answer: 'Apos os 7 dias de teste gratuito, voce escolhe o plano ideal e continua usando. Se nao quiser continuar, sua conta e desativada automaticamente.' },
    { question: 'Por que o SoluClinic custa esse valor?', answer: 'O plano Starter custa R$ 3,23 por dia — menos que um cafe. Se o sistema evitar uma unica falta de paciente por semana (ticket medio de R$ 150), voce recupera R$ 600/mes. O retorno sobre o investimento e de mais de 6x. Alem disso, voce economiza 8 a 10 horas por semana em tarefas administrativas que passam a ser automatizadas.' },
  ];

  return (
    <section id="faq" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Perguntas Frequentes
          </h2>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-4 sm:p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 text-sm sm:text-base pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`text-gray-500 transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} 
                  size={20} 
                />
              </button>
              {openIndex === index && (
                <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600 text-sm sm:text-base">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTA = () => (
  <section id="cta-final" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-dark-900 to-dark-800 text-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <img src="/logo-light.png" alt="SoluClinic" className="h-12 sm:h-16 mx-auto mb-6 sm:mb-8" />
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
        Pronto para transformar sua clinica?
      </h2>
      <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8">
        Comece agora com 7 dias gratis. Sem cartao de credito, sem compromisso.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <button 
          onClick={scrollToPrecos}
          className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-primary-400 to-primary-600 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold text-lg sm:text-xl hover:shadow-2xl hover:scale-105 transition-all"
        >
          <Zap size={24} />
          Testar 7 Dias Gratis
        </button>
        <a 
          href={WHATSAPP_DEMO}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            SoluClinicEvents.ClickDemo();
            SoluClinicEvents.ClickWhatsApp('Demo');
          }}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
        >
          <MessageCircle size={16} />
          Ou fale conosco no WhatsApp
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-dark-900 text-gray-400 py-8 sm:py-12 border-t border-gray-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <img src="/logo-light.png" alt="SoluClinic" className="h-8 sm:h-10" />
        
        <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
          <span className="hover:text-white transition cursor-pointer">Termos de Uso</span>
          <span className="hover:text-white transition cursor-pointer">Política de Privacidade</span>
          <span className="hover:text-white transition cursor-pointer">Contato</span>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <a 
            href={WHATSAPP_DEMO}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => SoluClinicEvents.ClickWhatsApp('Footer')}
            className="p-2 bg-green-600 rounded-full hover:bg-green-500 transition"
          >
            <MessageCircle size={18} className="text-white" />
          </a>
          <a 
            href="tel:+5571992248597"
            className="p-2 bg-gray-700 rounded-full hover:bg-gray-600 transition"
          >
            <Phone size={18} className="text-white" />
          </a>
        </div>
      </div>
      
      <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-800 text-center text-xs sm:text-sm">
        <p>© 2026 SoluClinic - Todos os direitos reservados</p>
      </div>
    </div>
  </footer>
);

// WhatsApp Float Button
const WhatsAppFloat = () => (
  <a
    href={WHATSAPP_DEMO}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => SoluClinicEvents.ClickWhatsApp('Float Button')}
    className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50 bg-green-500 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:bg-green-600 hover:scale-110 transition-all animate-float"
    title="Fale conosco no WhatsApp"
  >
    <MessageCircle size={24} />
  </a>
);

// Main App
function App() {
  return (
    <div className="min-h-screen">
      <UrgencyBar />
      <Header />
      <Hero />
      <Problems />
      <Benefits />
      <Stats />
      <HowItWorks />
      <Features />
      <MobileSection />
      <Testimonials />
      <Differentials />
      <TargetAudience />
      <Guarantee />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
